import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { Row, Col } from "react-bootstrap";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import ItemService from "../../../../services/customer_appCommonService";
import ERFExtensionPaymentSection from "./ERFExtensionPaymentSection";
import ERFTravelSection from "./ERFTravelSection";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import Loader from "../../../../Components/Loader";
import DataTable from "react-data-table-component";

const tableHeadStyle = {
  headRow: { style: { backgroundColor: "#ebf0f5", fontWeight: "bold" } },
};

const fmtDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-GB") : "—";

const jobStatusBadge = (status) => {
  const cls =
    status === "Done"
      ? "bg-success"
      : status === "Not Done"
        ? "bg-danger"
        : "bg-warning text-dark";
  return <span className={`badge ${cls}`}>{status || "Pending"}</span>;
};

const assignmentColumns = [
  {
    name: "#",
    width: "60px",
    cell: (row, index) => index + 1,
  },
  {
    name: "Service Executive",
    width: "200px",
    cell: (row) => (
      <div className="m-1">
        <div className="fw-bold">{row.serviceProviderName || "—"}</div>
        {row.serviceProviderUserId && (
          <div className="text-muted small">{row.serviceProviderUserId}</div>
        )}
      </div>
    ),
  },
  {
    name: "Category",
    width: "170px",
    selector: (row) => row.serviceAssignCategory || "—",
    wrap: true,
  },
  {
    name: "Collect Money",
    width: "140px",
    cell: (row) =>
      row.shouldCollectMoney ? (
        <span className="badge bg-success">Yes</span>
      ) : (
        <span className="badge bg-secondary">No</span>
      ),
  },
  {
    name: "Assigned By",
    width: "150px",
    selector: (row) => row.assignedBy || "—",
  },
  {
    name: "Assigned On",
    width: "190px",
    selector: (row) => fmtDateTime(row.assignDateAndTime),
  },
  {
    name: "Job Status",
    width: "140px",
    cell: (row) => jobStatusBadge(row.jobStatus),
  },
  {
    name: "Remarks",
    width: "230px",
    selector: (row) => row.jobStatusRemarks || "—",
    wrap: true,
  },
];

const ERFExtensionJobDoneModal = ({ show, onHide, row, onSuccess }) => {
  const userData = useSelector((state) => state?.user);
  const currentUserId = userData?.data?.userId || "";
  const currentUserName =
    userData?.data?.firstName || userData?.data?.userId || "";

  const [loading, setLoading] = useState(false);
  const [erfRecord, setErfRecord] = useState(null);
  const [jobRemarks, setJobRemarks] = useState("");
  const [hasPaymentSaved, setHasPaymentSaved] = useState(false);
  const [hasUnsavedTravel, setHasUnsavedTravel] = useState(false);
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(true);

  // ── "Not Done" confirmation modal state ───────────────────────────────────
  const [showNotDoneModal, setShowNotDoneModal] = useState(false);
  const [notDoneRemarks, setNotDoneRemarks] = useState("");

  // ── Role detection ────────────────────────────────────────────────────────
  const isServiceExecutive =
    userData?.data?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") ??
    false;

  const isDispatchManager =
    userData?.data?.roles?.some((r) => r.roleId === "DISPATCH") ?? false;

  const isFinanceExecutive =
    userData?.data?.roles?.some((r) => r.roleId === "FI-EXECUTIVE") ?? false;

  // ── Is money collection required for this job? ────────────────────────────
  // Payment is mandatory UNLESS it is explicitly a hospital ERF AND the
  // assignment was not flagged to collect money. In an extension the items
  // were already delivered at ERF creation — this step is only about whether
  // the SE must collect money (patient ERF can; hospital ERF may not).
  // shouldCollectMoney is read from the LAST assignment entry (the SE can be
  // re-assigned multiple times after a "Not Done").
  const lastAssignment = (erfRecord?.assignedServiceExecutives || []).at(-1);
  const shouldCollectMoney = lastAssignment?.shouldCollectMoney ?? true;
  const isHospitalErf = erfRecord?.erfForType === "Hospital";
  const paymentRequired = !(isHospitalErf && shouldCollectMoney === false);

  // ── Fetch fresh record when modal opens ───────────────────────────────────
  const fetchRecord = useCallback(async () => {
    if (!row?.documentNumber) return;
    setLoading(true);
    try {
      const res = await ItemService.generateQuery({
        queryName: "ERFExtDetail",
        documentNumber: row.documentNumber,
        timeZone: "Asia/Calcutta",
      });

      const record =
        (Array.isArray(res?.data) && res.data[0]) ||
        (Array.isArray(res?.data?.data) && res.data.data[0]) ||
        null;

      if (record) {
        setErfRecord(record);
        const alreadyHasPayment = (record.paymentHistory || []).some(
          (r) => parseFloat(r.billingReceivedAmt) > 0,
        );
        setHasPaymentSaved(alreadyHasPayment);
      } else {
        toast.error("Extension record not found");
      }
    } catch (err) {
      toast.error("Failed to load extension record");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [row?.documentNumber]);

  useEffect(() => {
    if (show) {
      fetchRecord();
      setJobRemarks("");
      setNotDoneRemarks("");
      setHasUnsavedTravel(false);
      setShowNotDoneModal(false);
    }
  }, [show, fetchRecord]);

  // ── Payment section notifies us when payment is saved ─────────────────────
  const handlePaymentSaved = useCallback((savedHistory) => {
    const hasReceived = (savedHistory || []).some(
      (r) => parseFloat(r.billingReceivedAmt) > 0,
    );
    setHasPaymentSaved(hasReceived);
  }, []);

  // ── Travel section handlers ───────────────────────────────────────────────
  const handleTravelSave = async (rowsToSave) => {
    if (!erfRecord) return;
    try {
      const updatedTravel = [...(erfRecord.travelHistory || []), ...rowsToSave];
      await ItemService.updateExtendEquipmentRent({
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.documentNumber,
        equipmentNr: erfRecord.equipmentNr,
        travelHistory: updatedTravel,
      });
      toast.success("Travel entries saved");
      await fetchRecord();
    } catch (err) {
      toast.error("Failed to save travel entries");
      throw err;
    }
  };

  const handleTravelConfirmRow = async (rowId, stage, { remarks }) => {
    if (!erfRecord) return;
    const confirmationKey = {
      se: "seConfirmation",
      dispatch: "dispatchConfirmation",
      finance: "financeConfirmation",
    }[stage];

    const updatedTravel = (erfRecord.travelHistory || []).map((r) =>
      r._id === rowId
        ? {
            ...r,
            [confirmationKey]: {
              confirmed: true,
              confirmedBy: currentUserName,
              confirmedAt: new Date().toISOString(),
              remarks,
            },
          }
        : r,
    );

    try {
      await ItemService.updateExtendEquipmentRent({
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.documentNumber,
        equipmentNr: erfRecord.equipmentNr,
        travelHistory: updatedTravel,
      });
      toast.success("Travel entry confirmed");
      await fetchRecord();
    } catch {
      toast.error("Failed to confirm travel entry");
    }
  };

  // ── Mark Payment Collected (Job Done) ─────────────────────────────────────
  const handleMarkDone = async () => {
    if (!erfRecord) return;

    if (erfRecord.erfStatusCode !== "REUSE") {
      toast.error(
        "Can only mark payment collected when status is 'RE Under Service Executive'.",
      );
      return;
    }
    // if (!hasPaymentSaved) {
    //   toast.error(
    //     "Please save at least one payment entry before marking as collected.",
    //   );
    //   return;
    // }
    if (paymentRequired && !hasPaymentSaved) {
      toast.error(
        "Please save at least one payment entry before marking the job done.",
      );
      return;
    }
    if (hasUnsavedTravel) {
      toast.error("You have unsaved travel entries. Please save them first.");
      return;
    }
    if (!jobRemarks.trim()) {
      toast.error("Please enter remarks before marking the job done.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.documentNumber,
        equipmentNr: erfRecord.equipmentNr,

        // Status: REUSE → REPAYCOLLECTED
        erfStatus: "RE Payment Collected",
        erfStatusCode: "REPAYCOLLECTED",

        jobDoneStatus: "Done",
        jobDoneRemarks: jobRemarks,
        jobDoneUpdatedAt: new Date().toISOString(),
        jobDoneUpdatedBy: currentUserId,

        // Mark last assignment entry's jobStatus as Done
        assignedServiceExecutives: (
          erfRecord.assignedServiceExecutives || []
        ).map((entry, idx, arr) =>
          idx === arr.length - 1
            ? { ...entry, jobStatus: "Done", jobStatusRemarks: jobRemarks }
            : entry,
        ),
      };

      await ItemService.updateExtendEquipmentRent(payload);
      // toast.success("Payment marked as collected successfully");
      toast.success(
        paymentRequired
          ? "Payment marked as collected successfully"
          : "Job marked as done successfully",
      );

      onSuccess?.();
      onHide();
    } catch (err) {
      toast.error("Failed to update status");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Mark Job Not Done ─────────────────────────────────────────────────────
  // Does NOT change erfStatus — only updates jobDoneStatus + reverts to PAYPENDING
  // so Dispatch Manager can re-assign a new SE.
  const handleMarkNotDone = async () => {
    if (!erfRecord) return;
    if (!notDoneRemarks.trim()) {
      toast.error("Please enter remarks for why the job is not done.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.documentNumber,
        equipmentNr: erfRecord.equipmentNr,

        // ── Revert erfStatus to PAYPENDING so DM can re-assign ──────────
        erfStatus: "Payment Pending",
        erfStatusCode: "PAYPENDING",

        jobDoneStatus: "Not Done",
        jobDoneRemarks: notDoneRemarks,
        jobDoneUpdatedAt: new Date().toISOString(),
        jobDoneUpdatedBy: currentUserId,

        // Mark last assignment entry's jobStatus as Not Done
        assignedServiceExecutives: (
          erfRecord.assignedServiceExecutives || []
        ).map((entry, idx, arr) =>
          idx === arr.length - 1
            ? {
                ...entry,
                jobStatus: "Not Done",
                jobStatusRemarks: notDoneRemarks,
                jobStatusUpdatedAt: new Date().toISOString(),
                jobStatusUpdatedBy: currentUserId,
              }
            : entry,
        ),
      };

      await ItemService.updateExtendEquipmentRent(payload);
      toast.success(
        "Job marked as Not Done. Dispatch Manager can now re-assign.",
      );
      setShowNotDoneModal(false);
      onSuccess?.();
      onHide();
    } catch (err) {
      toast.error("Failed to update job status");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isREUSE = erfRecord?.erfStatusCode === "REUSE";

  // SE can act only when REUSE
  const canCollect = isServiceExecutive && isREUSE;

  const showTravelSection =
    isServiceExecutive || isDispatchManager || isFinanceExecutive;

  return (
    <>
      {/* ── Main Payment Modal ───────────────────────────────────────────────── */}
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
            Payment Collection From SE — {row?.documentNumber}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Loader isLoading={loading} className="text-center" />

          {!loading && erfRecord && (
            <>
              {/* ── Summary bar ──────────────────────────────────────────── */}
              <Row className="mb-3 p-2 bg-light border rounded">
                <Col xs={12} md={4} className="mb-1">
                  <span className="text-muted small">Patient</span>
                  <p className="fw-bold mb-0">{erfRecord.patientName || "—"}</p>
                </Col>
                <Col xs={12} md={4} className="mb-1">
                  <span className="text-muted small">Original ERF</span>
                  <p className="fw-bold mb-0 text-primary">
                    {erfRecord.equipmentNr}
                  </p>
                </Col>
                <Col xs={12} md={4} className="mb-1">
                  <span className="text-muted small">Assigned SE</span>
                  <p className="fw-bold mb-0">
                    {erfRecord.serviceProviderName || "—"}
                  </p>
                </Col>
              </Row>

              {/* ── Assigned Service Executives ──────── */}
              <div className="accordion mb-3" id="accordionAssignmentHistory">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            showAssignmentHistory
                              ? "bi-chevron-down"
                              : "bi-chevron-right"
                          }`}
                          onClick={() => setShowAssignmentHistory((v) => !v)}
                        />
                      </Col>
                      <Col xs={1} className="text-center logoBtn">
                        <span className="iconLogo">A</span>
                      </Col>
                      <Col xs={10} md={8}>
                        <span className="headerTitle">
                          Assigned Service Executives
                        </span>
                      </Col>
                    </Row>
                  </h2>

                  <div
                    className={`accordion-collapse collapse show ${
                      showAssignmentHistory ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <DataTable
                        columns={assignmentColumns}
                        data={erfRecord.assignedServiceExecutives || []}
                        customStyles={tableHeadStyle}
                        noDataComponent={
                          <div className="p-2 text-muted text-center">
                            No service executives assigned yet
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Payment section ───────────────────────────────────────── */}
              <ERFExtensionPaymentSection
                key={erfRecord.documentNumber}
                documentNumber={erfRecord.documentNumber}
                allowAddBilling={canCollect}
                allowAddSecurity={false}
                allowAddCreditNote={false}
                allowUpdatePayentButton={canCollect}
                onPaymentSaved={handlePaymentSaved}
              />

              {/* ── Travel section (optional) ─────────────────────────────── */}
              {showTravelSection && (
                <ERFTravelSection
                  travelHistory={erfRecord.travelHistory || []}
                  erfStatusCode={erfRecord.erfStatusCode}
                  onSave={handleTravelSave}
                  onConfirmRow={handleTravelConfirmRow}
                  disabled={
                    !canCollect && !isDispatchManager && !isFinanceExecutive
                  }
                  onUnsavedRowsChange={setHasUnsavedTravel}
                />
              )}

              {/* ── SE Action section — visible only when canCollect ──────── */}
              {canCollect && (
                <>
                  {/* ── Mark Payment Collected ─────────────────────────────── */}
                  <div className="p-3 border rounded bg-light mt-3">
                    {/* <h6 className="text-primary fw-bold mb-3">
                      <i className="bi bi-check2-circle me-2" />
                      Mark Payment Collected
                    </h6>

                    {!hasPaymentSaved && (
                      <div className="alert alert-warning py-2 mb-3">
                        <i className="bi bi-exclamation-triangle me-2" />
                        Save at least one payment entry above before marking as
                        collected.
                      </div>
                    )}

                    {hasUnsavedTravel && (
                      <div className="alert alert-warning py-2 mb-3">
                        <i className="bi bi-exclamation-triangle me-2" />
                        You have unsaved travel entries. Save them first.
                      </div>
                    )}

                    <Row>
                      <Col
                        xs={12}
                        md={8}
                        className="commTopButtonRightLeftPadding"
                      >
                        <TextAreaField
                          className="inputBox"
                          label="Remarks *"
                          name="jobRemarks"
                          value={jobRemarks}
                          onChange={(e) => setJobRemarks(e.target.value)}
                          placeholder="Job done Remarks"
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        className="commTopButtonRightLeftPadding d-flex align-items-end"
                      >
                        <button
                          className="btnTable w-100"
                          onClick={handleMarkDone}
                          disabled={
                            loading ||
                            !hasPaymentSaved ||
                            !jobRemarks.trim() ||
                            hasUnsavedTravel
                          }
                        >
                          <i className="bi bi-check-circle me-2" />
                          {loading ? "Saving..." : "Mark Payment Collected"}
                        </button>
                      </Col> */}

                    <h6 className="text-primary fw-bold mb-3">
                      <i className="bi bi-check2-circle me-2" />
                      {paymentRequired
                        ? "Mark Payment Collected"
                        : "Mark Job Done"}
                    </h6>

                    {paymentRequired && !hasPaymentSaved && (
                      <div className="alert alert-warning py-2 mb-3">
                        <i className="bi bi-exclamation-triangle me-2" />
                        Save at least one payment entry above before marking the
                        job done.
                      </div>
                    )}

                    {hasUnsavedTravel && (
                      <div className="alert alert-warning py-2 mb-3">
                        <i className="bi bi-exclamation-triangle me-2" />
                        You have unsaved travel entries. Save them first.
                      </div>
                    )}

                    <Row>
                      <Col
                        xs={12}
                        md={8}
                        className="commTopButtonRightLeftPadding"
                      >
                        <TextAreaField
                          className="inputBox"
                          label="Remarks *"
                          name="jobRemarks"
                          value={jobRemarks}
                          onChange={(e) => setJobRemarks(e.target.value)}
                          placeholder="Job done Remarks"
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        className="commTopButtonRightLeftPadding d-flex align-items-end"
                      >
                        <button
                          className="btnTable w-100"
                          onClick={handleMarkDone}
                          disabled={
                            loading ||
                            (paymentRequired && !hasPaymentSaved) ||
                            !jobRemarks.trim() ||
                            hasUnsavedTravel
                          }
                        >
                          <i className="bi bi-check-circle me-2" />
                          {loading
                            ? "Saving..."
                            : paymentRequired
                              ? "Mark Payment Collected"
                              : "Mark Job Done"}
                        </button>
                      </Col>
                      <Row>
                        {/* ── Mark Job Not Done ──────────────────────────────────── */}
                        <Col
                          xs={12}
                          md={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <button
                            className="btnTable btn-danger"
                            onClick={() => setShowNotDoneModal(true)}
                            disabled={loading}
                          >
                            <i className="bi bi-x-circle me-2" />
                            Mark as Not Done
                          </button>
                        </Col>
                      </Row>
                    </Row>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Col xs={4} md={4} className="commTopButtonRightLeftPadding">
            <button className="btnTable btn-secondary" onClick={onHide}>
              Close
            </button>
          </Col>
        </Modal.Footer>
      </Modal>

      {/* ── "Not Done" Confirmation Modal ───────────────────────────────────── */}
      <Modal
        show={showNotDoneModal}
        onHide={() => {
          setShowNotDoneModal(false);
          setNotDoneRemarks("");
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-x-circle me-2" />
            Mark Job as Not Done
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label fw-bold">
            Reason / Remarks <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={notDoneRemarks}
            onChange={(e) => setNotDoneRemarks(e.target.value)}
            placeholder="Enter the reason why the job could not be completed..."
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btnTable btn-secondary"
            onClick={() => {
              setShowNotDoneModal(false);
              setNotDoneRemarks("");
            }}
          >
            Cancel
          </button>
          <button
            className="btnTable btn-danger"
            onClick={handleMarkNotDone}
            disabled={loading || !notDoneRemarks.trim()}
          >
            {loading ? "Saving..." : "Confirm — Not Done"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ERFExtensionJobDoneModal;
